// @ts-check
/**
 * One collapsible section of the report, with a rule above it.
 *
 * Open by default: the report is short and its parts should not have to be discovered.
 */
import React, { useId, useState } from 'react';
import PropTypes from 'prop-types';
import { LuChevronDown } from 'react-icons/lu';

import { TestableAccordion, TestableAccordionBody, TestableAccordionHeader } from '@components/general/TestableComponents';

/**
 * @param {Object} props
 * @param {string} props.title
 * @param {string} props.dataTestId
 * @param {boolean} [props.defaultOpen]
 * @param {import('react').ReactNode} props.children
 */
const AIReportSection = ({ title, dataTestId, defaultOpen = true, children }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const bodyId = useId();

  return (
    <TestableAccordion
      dataTestId={dataTestId}
      open={isOpen}
      className="border-t border-gray-200 dark:border-gray-700"
      icon={<LuChevronDown aria-hidden="true" className={`h-5 w-5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />}
    >
      {/* h4 rather than h2/h3: those carry the app's display styling, which is far too loud in a panel. */}
      <h4 className="m-0">
        {/* The header sets no ARIA of its own, so the disclosure state is supplied here. */}
        <TestableAccordionHeader
          dataTestId={`${dataTestId}-toggle`}
          aria-expanded={isOpen}
          aria-controls={bodyId}
          onClick={() => setIsOpen((open) => !open)}
          className="border-b-0 py-3 text-base font-semibold text-gray-900 hover:text-gray-900 dark:text-gray-100"
        >
          {title}
        </TestableAccordionHeader>
      </h4>

      {/*
        Children are mounted only while open. The body animates its height to zero rather than hiding,
        so anything left inside stays in the accessibility tree and keeps its links tabbable.
      */}
      <TestableAccordionBody dataTestId={`${dataTestId}-body`} id={bodyId} className="pt-0 pb-5">
        {isOpen && children}
      </TestableAccordionBody>
    </TestableAccordion>
  );
};

AIReportSection.propTypes = {
  title: PropTypes.string.isRequired,
  dataTestId: PropTypes.string.isRequired,
  defaultOpen: PropTypes.bool,
  children: PropTypes.node,
};

export default AIReportSection;
